import React, { useRef, useState, useEffect, Suspense, lazy } from 'react';
import { NavLink } from 'react-router-dom';
import { ReactComponent as Logo } from '../assets/logo/zecado.svg';

// Below-the-fold icon set - kept out of the entry bundle
const PaymentMethods = lazy(() => import('../components/icons/PaymentMethods'));

function Footer() {
  const form = useRef();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isVisible, setIsVisible] = useState(false);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const footerRef = useRef(null);

  // Intersection Observer for lazy animations
  useEffect(() => {
    if (!footerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            // Delay animation start slightly for better performance
            setTimeout(() => setShouldAnimate(true), 100);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: '100px',
        threshold: 0.1
      }
    );

    observer.observe(footerRef.current);

    return () => observer.disconnect();
  }, []);

  const sendEmail = async (e) => {
    e.preventDefault();
    setLoading(true);
    setShowErrorAlert(false);
    setErrorMessage('');

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}rest/public/newsletter/subscribe`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email, source: 'footer' }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        setLoading(false);
        setShowSuccessAlert(true);
        setEmail('');
        form.current.reset();
        setTimeout(() => {
          setShowSuccessAlert(false);
        }, 5000);
      } else {
        setLoading(false);
        setShowErrorAlert(true);
        setErrorMessage(data.message || 'Failed to subscribe. Please try again.');
        setTimeout(() => {
          setShowErrorAlert(false);
        }, 5000);
      }
    } catch (error) {
      setLoading(false);
      setShowErrorAlert(true);
      setErrorMessage('An error occurred. Please try again later.');
      console.error('Subscription error:', error);
      setTimeout(() => {
        setShowErrorAlert(false);
      }, 5000);
    }
  };

  return (
    <footer 
      ref={footerRef}
      className="relative overflow-hidden bg-black"
      style={{
        background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #000000 100%)',
      }}
    >
      {/* Animated Background Elements - Only render when visible */}
      {shouldAnimate && (
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          {[...Array(15)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 rounded-full animate-pulse"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${3 + Math.random() * 2}s`,
                backgroundColor: '#c5945a',
                boxShadow: '0 0 20px #c5945a',
              }}
            />
          ))}
        </div>
      )}

      {/* Gradient Overlays */}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-60 pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#c5945a]/5 to-transparent pointer-events-none" />

      {/* Main Content */}
      <div className="relative mx-auto w-full max-w-7xl px-6 py-16 lg:px-8 lg:py-24">
        
        {/* Logo Section */}
        <div className={`mb-16 transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          <NavLink to="/" className="inline-block group">
            <div className="transform transition-all duration-500 group-hover:scale-105">
              <Logo 
                id="logo" 
                fill="#c5945a" 
                className="h-8 md:h-12 filter drop-shadow-lg"
              />
            </div>
          </NavLink>
        </div>

        {/* Main Grid */}
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-20 xl:gap-32 items-start">
          
          {/* Newsletter Section */}
          <div className={`space-y-8 transform transition-all duration-1000 delay-200 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            
            {/* Premium Badge */}
            <div className="inline-flex items-center px-4 py-2 border border-white/20 rounded-full backdrop-blur-sm bg-white/5">
              <span className="text-xs font-light tracking-[0.2em] text-white/80 uppercase">
                Stay Connected
              </span>
            </div>

            {/* Newsletter Title */}
            <div className="space-y-4">
              <h3 className="text-2xl md:text-3xl lg:text-4xl font-thin tracking-tight text-white leading-tight">
                Subscribe to our
                <span className="block bg-gradient-to-r from-white via-[#c5945a] to-white bg-clip-text text-transparent">
                  Newsletter
                </span>
              </h3>
              <p className="text-white/60 text-sm md:text-base leading-relaxed max-w-md">
                Be the first to discover our latest fragrances and exclusive collections.
              </p>
            </div>

            {/* Newsletter Form */}
            <form ref={form} onSubmit={sendEmail} className="space-y-4">
              <div className="relative group">
                <input
                  type="email"
                  name="user_email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-transparent border-b-2 border-white/20 pb-3 text-white placeholder-white/40 text-lg focus:border-[#c5945a] focus:outline-none transition-all duration-500 group-hover:border-white/30"
                  placeholder="Enter your email address"
                  required
                />
                <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#c5945a] transition-all duration-500 group-focus-within:w-full" />
              </div>
              
              <button
                type="submit"
                disabled={loading}
                className="group relative inline-flex items-center justify-center px-8 py-4 text-black font-medium tracking-[0.2em] uppercase text-sm transition-all duration-500 hover:bg-transparent hover:text-white overflow-hidden"
                style={{ backgroundColor: '#c5945a' }}
              >
                <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></span>
                <span className="absolute inset-0 border opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ borderColor: '#c5945a' }}></span>
                
                {loading ? (
                  <div className="flex items-center space-x-2">
                    <svg
                      className="animate-spin h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v8h8a8 8 0 11-16 0z"
                      ></path>
                    </svg>
                    <span className="relative z-10">Subscribing...</span>
                  </div>
                ) : (
                  <span className="relative z-10">Subscribe Now</span>
                )}
              </button>

              {/* Success Message */}
              {showSuccessAlert && (
                <div className="flex items-center space-x-2 text-[#c5945a] text-sm animate-fade-in">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Thank you for subscribing to our newsletter!</span>
                </div>
              )}

              {/* Error Message */}
              {showErrorAlert && (
                <div className="flex items-center space-x-2 text-red-400 text-sm animate-fade-in">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <span>{errorMessage}</span>
                </div>
              )}
            </form>
          </div>

          {/* Links Section */}
          <div className={`grid grid-cols-1 sm:grid-cols-3 gap-8 lg:gap-12 transform transition-all duration-1000 delay-400 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            
            {/* Services */}
            <div className="space-y-6">
              <h4 className="text-sm font-medium text-white/90 uppercase tracking-[0.2em]">
                Services
              </h4>
              <ul className="space-y-4">
                {[
                  { to: '/shop', label: 'Shop' },
                  { to: '/cart', label: 'Cart' },
                  { to: '/about', label: 'About' }
                ].map((link) => (
                  <li key={link.to}>
                    <NavLink 
                      to={link.to} 
                      className="group inline-flex items-center text-white/60 hover:text-[#c5945a] transition-all duration-300"
                    >
                      <span className="mr-2 w-4 h-px bg-white/20 transform group-hover:w-8 group-hover:bg-[#c5945a] transition-all duration-300"></span>
                      {link.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>

            {/* Social */}
            <div className="space-y-6">
              <h4 className="text-sm font-medium text-white/90 uppercase tracking-[0.2em]">
                Follow Us
              </h4>
              <ul className="space-y-4">
                {[
                  { href: 'https://www.instagram.com/zecado_/?api=1', label: 'Instagram' },
                  { href: 'https://www.tiktok.com/@zecadofragrance', label: 'TikTok' }
                ].map((social) => (
                  <li key={social.href}>
                    <a 
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group inline-flex items-center text-white/60 hover:text-[#c5945a] transition-all duration-300"
                    >
                      <span className="mr-2 w-4 h-px bg-white/20 transform group-hover:w-8 group-hover:bg-[#c5945a] transition-all duration-300"></span>
                      {social.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Legal */}
            <div className="space-y-6">
              <h4 className="text-sm font-medium text-white/90 uppercase tracking-[0.2em]">
                Legal
              </h4>
              <ul className="space-y-4">
                {[
                  { to: '/privacy-policy', label: 'Privacy Policy' },
                  { to: '/terms-and-conditions', label: 'Terms & Conditions' }
                ].map((link) => (
                  <li key={link.to}>
                    <NavLink 
                      to={link.to} 
                      className="group inline-flex items-center text-white/60 hover:text-[#c5945a] transition-all duration-300"
                    >
                      <span className="mr-2 w-4 h-px bg-white/20 transform group-hover:w-8 group-hover:bg-[#c5945a] transition-all duration-300"></span>
                      {link.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Payment Methods Section */}
        <div className={`mt-16 mb-8 transform transition-all duration-1000 delay-600 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          <div className="text-center space-y-6">
            <h4 className="text-sm font-medium text-white/90 uppercase tracking-[0.2em]">
              We Accept
            </h4>
            
            {/* Reserve the row's height so the lazy chunk can't shift layout */}
            <Suspense fallback={<div className="min-h-[136px]" aria-hidden="true" />}>
              <PaymentMethods />
            </Suspense>
          </div>
        </div>

        {/* Decorative Line */}
        <div className={`mb-8 transform transition-all duration-1000 delay-800 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          <div className="w-full h-px bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
        </div>

        {/* Copyright */}
        <div className={`text-center transform transition-all duration-1000 delay-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          <p className="text-white/50 text-sm tracking-wide">
            © Copyright 2024 <span className="text-[#c5945a] font-medium">Zecado™</span>. All Rights Reserved.
          </p>
        </div>
      </div>

      {/* Bottom Accent */}
      <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#c5945a] to-transparent"></div>
    </footer>
  );
}

export default Footer;